/* ==========================================================================
   IRON FIT — the navbar's behaviour. Loaded on every page.

   Three small jobs:

     1. the pill leans toward the cursor, in 3D
     2. the bar grows a background once you scroll off the top
     3. on the homepage, the Machines and Packages links become smooth
        in-page jumps instead of navigations

   The tilt is pointer-only. A phone reports a hover it cannot perform, so
   a tilt bound to hover latches on first touch and never lets go. It is
   also skipped outright for anyone who asked for reduced motion — the
   colour changes stay, only the movement goes.
   ========================================================================== */
(function () {
  'use strict';

  var nav = document.querySelector('.rnav');
  if (!nav) return;

  var fine = window.matchMedia &&
             window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var still = window.matchMedia &&
              window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------
     1. THE TILT

     Rotation is capped low on purpose. The pill is a navigation bar with
     six words in it; past about eight degrees the far end of it starts to
     become genuinely harder to read, and a menu that has to be squinted
     at has stopped being a menu.
     ------------------------------------------------------------------ */
  var pill = nav.querySelector('.nav__links');

  if (pill && fine && !still) {
    var MAX = 7;                           /* degrees */
    var frame = null;

    function lean(e) {
      if (frame) return;                   /* one update per painted frame */
      frame = window.requestAnimationFrame(function () {
        frame = null;
        var r = pill.getBoundingClientRect();
        if (!r.width || !r.height) return;
        var x = (e.clientX - r.left) / r.width  - .5;   /* -0.5 … 0.5 */
        var y = (e.clientY - r.top)  / r.height - .5;
        pill.style.setProperty('--ry', (x *  2 * MAX).toFixed(2) + 'deg');
        pill.style.setProperty('--rx', (y * -2 * MAX).toFixed(2) + 'deg');
      });
    }

    function level() {
      if (frame) { window.cancelAnimationFrame(frame); frame = null; }
      pill.classList.remove('is-tilt');
      pill.style.setProperty('--rx', '0deg');
      pill.style.setProperty('--ry', '0deg');
    }

    pill.addEventListener('pointerenter', function (e) {
      if (e.pointerType !== 'mouse') return;
      pill.classList.add('is-tilt');
      lean(e);
    });
    pill.addEventListener('pointermove', function (e) {
      if (e.pointerType !== 'mouse') return;
      lean(e);
    });
    pill.addEventListener('pointerleave', level);
    /* a pointer that vanishes mid-tilt (tab switch, window blur) must not
       leave the bar frozen at an angle */
    window.addEventListener('blur', level);
  }

  /* ------------------------------------------------------------------
     2. THE BACKGROUND

     Only the overlay bar needs this — the solid one already has it. Both
     are handled here anyway so no page has to remember which it is.
     ------------------------------------------------------------------ */
  /* HOW FAR DOWN ARE WE, really.

     window.pageYOffset is not the answer on every phone. Measured on this
     site at a phone width: the hero had moved to top: -1500 while pageYOffset
     still read 0, because the page was scrolling inside an element rather
     than the window. Whichever of the three is moving, the largest wins. */
  function scrolled() {
    return Math.max(
      window.pageYOffset || 0,
      document.documentElement.scrollTop || 0,
      document.body.scrollTop || 0
    );
  }

  /* Nothing here holds the bar up any more — position: sticky does, and the
     stylesheet explains why it had to. Two attempts are gone with it: a
     transform that pushed the bar back to top: 0, which was useless because
     getBoundingClientRect() reports the same broken coordinates that moved it
     in the first place, and which would now break stickiness outright; and an
     inline width pinned from clientWidth, which sticky makes unnecessary by
     taking its width from the parent in normal flow.

     What is left is the background, which still needs to know whether you
     have scrolled off the top. */
  var ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      ticking = false;
      nav.classList.toggle('is-stuck', scrolled() > 40);
    });
  }

  /* A scroll inside an element does not reach window, so the document is
     listened to in the capture phase as well — that catches every scroller
     on the page, whichever one turns out to be the real one. */
  window.addEventListener('scroll', onScroll, { passive: true });
  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('resize', onScroll, { passive: true });
  window.addEventListener('orientationchange', onScroll, { passive: true });
  onScroll();

  /* ------------------------------------------------------------------
     3. THE PHONE MENU

     The sheet is real markup, so it is in the page for a crawler and for
     anyone whose JavaScript never arrives — this only opens and closes it.
     Both it and the veil ship with `hidden` so they cannot flash on screen
     before the stylesheet lands; that attribute comes off here, once
     something is able to close them again.
     ------------------------------------------------------------------ */
  var mbtn  = nav.querySelector('.rnav__menu');
  var sheet = nav.querySelector('.rnav__sheet');
  var veil  = nav.querySelector('.rnav__veil');

  if (mbtn && sheet && veil) {
    sheet.hidden = false;
    veil.hidden = false;

    function setMenu(on) {
      mbtn.classList.toggle('is-on', on);
      sheet.classList.toggle('is-on', on);
      veil.classList.toggle('is-on', on);
      mbtn.setAttribute('aria-expanded', on ? 'true' : 'false');
      /* The page must not scroll behind an open sheet, but the lock has to
         be lifted by anything that closes it — including a link inside it. */
      document.documentElement.style.overflow = on ? 'hidden' : '';
    }

    mbtn.addEventListener('click', function () {
      setMenu(!sheet.classList.contains('is-on'));
    });
    veil.addEventListener('click', function () { setMenu(false); });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sheet.classList.contains('is-on')) {
        setMenu(false);
        try { mbtn.focus({ preventScroll: true }); } catch (err) {}
      }
    });

    /* Every link closes it. An in-page one would otherwise scroll the page
       underneath a sheet still covering it. */
    [].forEach.call(sheet.querySelectorAll('a'), function (a) {
      a.addEventListener('click', function () { setMenu(false); });
    });

    /* Grown past a phone with the sheet open — close it, or the lock stays
       on a page that now has its full navigation back. */
    if (window.matchMedia) {
      var wide = window.matchMedia('(min-width: 1041px)');
      var onWide = function (e) { if (e.matches) setMenu(false); };
      if (wide.addEventListener) wide.addEventListener('change', onWide);
      else if (wide.addListener) wide.addListener(onWide);
    }

    /* mark where we are */
    var path = location.pathname.replace(/\/+$/, '');
    [].forEach.call(sheet.querySelectorAll('a[href]'), function (a) {
      var h = a.getAttribute('href');
      if (h.charAt(0) === '#' || h.indexOf('wa.me') > -1) return;
      if (h.replace(/#.*$/, '').replace(/\/+$/, '') === path && h.indexOf('#') === -1) {
        a.classList.add('is-here');
      }
    });
  }

  /* ------------------------------------------------------------------
     4. HOME LINKS THAT ARE ALREADY HOME

     The bar ships absolute links so they work from the cart and the
     package pages. On the homepage itself those same links would reload
     the page to land on an anchor it is already showing, so here they are
     turned back into scrolls.
     ------------------------------------------------------------------ */
  var here = location.pathname.replace(/\/+$/, '');
  [].forEach.call(nav.querySelectorAll('.nav__link[href*="#"]'), function (a) {
    var href = a.getAttribute('href') || '';
    var hash = href.slice(href.indexOf('#'));
    var path = href.slice(0, href.indexOf('#')).replace(/\/+$/, '');
    if (path !== here) return;                       /* a real navigation */

    a.addEventListener('click', function (e) {
      var target = document.querySelector(hash);
      if (!target) return;                           /* let it navigate */
      e.preventDefault();
      target.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
      [].forEach.call(nav.querySelectorAll('.nav__link'), function (o) {
        o.classList.remove('is-here');
      });
      a.classList.add('is-here');
      if (history.replaceState) history.replaceState(null, '', hash);
    });
  });
})();
